import React, { useState, useEffect } from 'react';
import { shippingRatesAPI } from '../lib/api';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Globe, 
  RefreshCw,
  MapPin,
  Phone,
  Mail,
  AlertTriangle,
  Package,
  Truck
} from 'lucide-react';
import { formatCurrency, countryNames } from '../lib/utils';

export default function RatesPage() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await shippingRatesAPI.getAll();
        setRates(res.data);
      } catch (error) {
        console.error('Error fetching rates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRates();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="rates-page">
      <Navbar />
      
      <main className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mb-4">
              Tarifs Colissimo International
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Nos tarifs sont basés sur les prix officiels La Poste/Colissimo.
              Le prix dépend du poids et de la destination de votre colis.
            </p>
          </div>

          {/* Important Notice for Heavy Packages */}
          <Card className="bg-amber-50 border-amber-200 mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-amber-800 mb-2">
                    Envois volumineux ou palettes
                  </h2>
                  <p className="text-amber-700 mb-4">
                    Pour les envois de <strong>plus de 30 kg</strong> ou les <strong>palettes</strong>, 
                    merci de nous contacter pour obtenir un devis personnalisé.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href="tel:+33695148686"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      06 95 14 86 86
                    </a>
                    <a 
                      href="mailto:contact@ismparcel.fr"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 font-medium rounded-lg transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      contact@ismparcel.fr
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rates Tables */}
          {rates.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-12 text-center">
                <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                  Tarifs en cours de configuration
                </h3>
                <p className="text-slate-600">
                  Nos tarifs seront bientôt disponibles. Contactez-nous pour un devis personnalisé.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {rates.map((rate) => (
                <Card key={rate.id || rate.zone_name} className="bg-white border-slate-200 overflow-hidden">
                  <CardHeader className="bg-slate-900 text-white py-4">
                    <CardTitle className="font-heading flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className="w-6 h-6 text-blue-400" />
                        <div>
                          <span className="text-xl">{rate.zone_name}</span>
                          {rate.description && (
                            <p className="text-sm text-slate-300 font-normal mt-1">
                              {rate.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-blue-600 text-white">
                        Max {rate.max_weight} kg
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Price Table */}
                    {rate.weight_prices && rate.weight_prices.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-sm font-bold text-slate-700 uppercase tracking-wider">
                                Poids jusqu'à
                              </th>
                              <th className="px-6 py-3 text-right text-sm font-bold text-slate-700 uppercase tracking-wider">
                                Prix TTC
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {rate.weight_prices.map((wp, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4 text-slate-900 font-medium">
                                  <div className="flex items-center gap-2">
                                    <Package className="w-4 h-4 text-slate-400" />
                                    {wp.max_kg} kg
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <span className="font-heading font-bold text-lg text-blue-600">
                                    {formatCurrency(wp.price)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-slate-50 rounded-lg p-4 text-center">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Prix de base</p>
                            <p className="font-heading font-bold text-2xl text-slate-900">
                              {formatCurrency(rate.base_price)}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-4 text-center">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Par kg supplémentaire</p>
                            <p className="font-heading font-bold text-2xl text-blue-600">
                              {formatCurrency(rate.price_per_kg)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Countries */}
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                      <p className="text-sm text-slate-500 mb-2 font-medium">Pays inclus :</p>
                      <div className="flex flex-wrap gap-2">
                        {rate.countries.map(code => (
                          <Badge key={code} variant="outline" className="text-sm bg-white">
                            {countryNames[code] || code}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                      Délais de livraison
                    </h3>
                    <ul className="text-slate-600 space-y-1 text-sm">
                      <li>• Europe : 3-5 jours ouvrés</li>
                      <li>• DOM-TOM : 5-10 jours ouvrés</li>
                      <li>• Afrique : 7-15 jours ouvrés</li>
                      <li>• Reste du monde : 7-15 jours ouvrés</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                      Dimensions maximales
                    </h3>
                    <ul className="text-slate-600 space-y-1 text-sm">
                      <li>• Longueur max : 150 cm</li>
                      <li>• L + l + h : max 300 cm</li>
                      <li>• Poids max standard : 30 kg</li>
                      <li>• Colis hors gabarit : +6 €</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Section */}
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white mt-12">
            <CardContent className="p-8 text-center">
              <h3 className="font-heading font-bold text-2xl mb-4">
                Envoi de plus de 30 kg ou palette ?
              </h3>
              <p className="text-blue-100 max-w-xl mx-auto mb-6">
                Pour les envois volumineux, réguliers ou les palettes, 
                contactez-nous pour un devis sur mesure adapté à vos besoins.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="tel:+33695148686"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  06 95 14 86 86
                </a>
                <a 
                  href="https://wa.me/33695148686"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-lg hover:bg-green-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
